import {
    AccessorialType,
    ACCESSORIAL_TYPE_LABEL,
    ADJUSTMENT_ACCESSORIAL_TYPES,
    InvoiceAdjustmentDecision,
    InvoiceAdjustmentKind,
    InvoiceAdjustmentLine,
    invoiceAdjustmentTypeLabel,
    summarizeAdjustmentLines,
} from '@numeo/types-core'
import { RateconRevisionInvoiceContext } from '@numeo/types-main-web-app'

/** One charge the dispatcher is drafting. `amount: null` means "untouched": the suggested difference is used when this is the
 *  only line. Billing nothing is the "Apply rate only" button, not a value here. */
export interface BillingLineDraft {
    amount: string | null
    kind: InvoiceAdjustmentKind | null
    /** The exact charge, when the kind is an additional charge. */
    accessorialType: AccessorialType | null
    note: string
}

/** The charges the dispatcher is drafting for an invoiced load's revision. Starts as one line — the whole difference as a line
 *  haul revision; the dispatcher can split it into named charges (which must still total the difference). */
export interface BillingDraft {
    lines: BillingLineDraft[]
}

export const EMPTY_BILLING_LINE: BillingLineDraft = { amount: null, kind: null, accessorialType: null, note: '' }
export const EMPTY_BILLING_DRAFT: BillingDraft = { lines: [EMPTY_BILLING_LINE] }

const CHARGE_PREFIX = 'charge:'

/** What the Type select holds: the line haul revision, or one exact charge (`charge:DETENTION`). */
export const billingTypeValue = (kind: InvoiceAdjustmentKind | 'unset', accessorialType: AccessorialType | null): string => {
    if (kind === 'unset') return ''
    if (kind === InvoiceAdjustmentKind.ADDITIONAL_CHARGE) return `${CHARGE_PREFIX}${accessorialType ?? AccessorialType.OTHER}`
    return kind
}

/** The line a Type choice makes: the kind, and the exact charge when it is one. */
export const applyBillingType = (line: BillingLineDraft, value: string): BillingLineDraft => {
    if (value.startsWith(CHARGE_PREFIX)) {
        return { ...line, kind: InvoiceAdjustmentKind.ADDITIONAL_CHARGE, accessorialType: value.slice(CHARGE_PREFIX.length) as AccessorialType }
    }
    return { ...line, kind: value as InvoiceAdjustmentKind, accessorialType: null }
}

/** The Type select's choices: the revision of the line haul, then each exact charge a broker is billed for. */
export const BILLING_TYPE_SECTIONS = [
    { items: [{ label: 'Line haul revision', value: InvoiceAdjustmentKind.LINE_HAUL_REVISION }] },
    { title: 'Charge', items: ADJUSTMENT_ACCESSORIAL_TYPES.map((type) => ({ label: ACCESSORIAL_TYPE_LABEL[type], value: `${CHARGE_PREFIX}${type}` })) },
]

export type BillingResolution = { ok: true; adjustment: InvoiceAdjustmentDecision } | { ok: false; reason: string }

const cents = (value: number): number => Math.round(value * 100) / 100

export const formatUsd = (amount: number): string => `$${Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export const formatSignedUsd = (amount: number): string => `${amount < 0 ? '-' : '+'}${formatUsd(amount)}`

/** The difference the server would suggest for this rate, or null when it cannot: the rate did not change, or the load
 *  was re-rated after it was invoiced so the billed amount is not the revision's baseline. */
const autoAmount = (context: RateconRevisionInvoiceContext, rateChanged: boolean, effectiveRate: number): number | null =>
    rateChanged && context.confident ? cents(effectiveRate - context.billedAmount) : null

/** Whether the rate accepting writes differs from what was already billed — i.e. there is a difference worth suggesting
 *  as a charge by default. False means nothing to suggest; the dispatcher can still add a charge by hand. */
export const needsBilling = (context: RateconRevisionInvoiceContext, rateChanged: boolean, effectiveRate: number): boolean =>
    rateChanged && cents(effectiveRate) !== cents(context.billedAmount)

/** The amount a line shows: what was typed, else — when it is the only line — the suggested difference, else empty. */
export const lineAmountText = (
    line: BillingLineDraft,
    context: RateconRevisionInvoiceContext,
    rateChanged: boolean,
    effectiveRate: number,
    isSoleLine: boolean
): string => {
    if (line.amount !== null) return line.amount
    if (!isSoleLine) return ''
    const auto = autoAmount(context, rateChanged, effectiveRate)
    return auto ? String(auto) : ''
}

/** A line's charge type: what was chosen, else a line haul revision for the first line of a rate change, else nothing yet. */
export const lineKind = (line: BillingLineDraft, rateChanged: boolean, isFirst: boolean): InvoiceAdjustmentKind | 'unset' => {
    if (line.kind) return line.kind
    if (rateChanged && isFirst) return InvoiceAdjustmentKind.LINE_HAUL_REVISION
    return 'unset'
}

type ResolvedLine = { ok: true; line: InvoiceAdjustmentLine } | { ok: false; reason: string }

const resolveLine = (
    line: BillingLineDraft,
    index: number,
    context: RateconRevisionInvoiceContext,
    rateChanged: boolean,
    effectiveRate: number,
    isSoleLine: boolean
): ResolvedLine => {
    const kind = lineKind(line, rateChanged, index === 0)
    if (kind === 'unset') return { ok: false, reason: 'Choose what each charge is for.' }
    const text = lineAmountText(line, context, rateChanged, effectiveRate, isSoleLine).trim()
    if (!text) return { ok: false, reason: 'Enter the amount for each charge.' }
    const amount = Number(text)
    if (!Number.isFinite(amount) || cents(amount) === 0) return { ok: false, reason: 'Each amount must be a number other than zero.' }
    const note = line.note.trim()
    const accessorialType = kind === InvoiceAdjustmentKind.ADDITIONAL_CHARGE ? (line.accessorialType ?? AccessorialType.OTHER) : null
    return { ok: true, line: { kind, amount: cents(amount), ...(accessorialType && { accessorialType }), ...(note && { note }) } }
}

/** The running total of the lines as typed, for the panel to show against the difference (invalid amounts count as 0). */
export const billingLinesTotal = (draft: BillingDraft, context: RateconRevisionInvoiceContext, rateChanged: boolean, effectiveRate: number): number => {
    const sole = draft.lines.length === 1
    return cents(
        draft.lines.reduce((sum, line) => {
            const amount = Number(lineAmountText(line, context, rateChanged, effectiveRate, sole).trim())
            return sum + (Number.isFinite(amount) ? amount : 0)
        }, 0)
    )
}

/** The charges the fields resolve to, or why they are not ready. A single line is billed as today (no `lines`); several lines
 *  must total the suggested difference exactly — the difference is split into named charges, never re-sized. Billing nothing is
 *  the Apply-rate-only button, never a resolution here. */
export const resolveBilling = (draft: BillingDraft, context: RateconRevisionInvoiceContext, rateChanged: boolean, effectiveRate: number): BillingResolution => {
    const sole = draft.lines.length === 1
    const resolved: InvoiceAdjustmentLine[] = []
    for (let index = 0; index < draft.lines.length; index++) {
        const line = resolveLine(draft.lines[index], index, context, rateChanged, effectiveRate, sole)
        if (!line.ok) return { ok: false, reason: line.reason }
        resolved.push(line.line)
    }
    const net = cents(resolved.reduce((sum, line) => sum + line.amount, 0))
    if (net === 0) return { ok: false, reason: 'The amount must be a number other than zero.' }
    // One charge is billed freely (a manual detention, or the whole difference). Several are a split, so they must total the
    // difference and point the same way.
    if (sole) return { ok: true, adjustment: resolved[0] }
    const difference = autoAmount(context, rateChanged, effectiveRate)
    if (difference === null) return { ok: false, reason: "This load's difference cannot be worked out, so it cannot be split into charges." }
    if (net !== cents(difference)) return { ok: false, reason: `The charges must total ${formatSignedUsd(difference)} — they total ${formatSignedUsd(net)}.` }
    if (resolved.some((line) => Math.sign(line.amount) !== Math.sign(net)))
        return { ok: false, reason: 'Every charge must be in the same direction: all billed or all credited.' }
    return { ok: true, adjustment: { ...summarizeAdjustmentLines(resolved), lines: resolved } }
}

/** The line under the fields: how the amount came about and what accepting does with it. */
export const describeBillingOutcome = (
    context: RateconRevisionInvoiceContext,
    resolution: BillingResolution,
    rateChanged: boolean,
    effectiveRate: number
): string => {
    if (!resolution.ok) return resolution.reason
    const { amount, kind, lines } = resolution.adjustment
    const word = amount < 0 ? 'credit' : 'charge'
    if (lines && lines.length > 1) {
        return `${lines.length} charges totaling ${formatSignedUsd(amount)} will wait in Ready to Invoice.`
    }
    // The arithmetic explains a line haul revision only: a detention or a lumper is not a difference of two rates.
    if (kind === InvoiceAdjustmentKind.LINE_HAUL_REVISION && rateChanged) {
        return `Revised ${formatUsd(effectiveRate)} − billed ${formatUsd(context.billedAmount)} = ${formatSignedUsd(cents(effectiveRate - context.billedAmount))}. ${formatSignedUsd(amount)} ${word} will wait in Ready to Invoice.`
    }
    const label = invoiceAdjustmentTypeLabel({ kind, accessorialType: resolution.adjustment.accessorialType })
    return `${formatSignedUsd(amount)} ${kind === InvoiceAdjustmentKind.ADDITIONAL_CHARGE ? `${label} ` : ''}${word} will wait in Ready to Invoice.`
}
